import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, lastValueFrom } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { Organisation } from '../interfaces/organisation.interface';
import { Role } from '../interfaces/role.interface';
import { DashboardStats } from '../interfaces/dashboard-stats.interface';
import { ApiResponse } from '../interfaces/api-respone.interface';
import { AuthenticatedUser } from '../interfaces/authenticated-user.interface';

@Injectable({
    providedIn: 'root'
})

export class AuthenticationService {
    private LOGIN_ENDPOINT = 'auth/login';
    private GET_USER_PROFILE_ENDPOINT = 'user/profile';
    private VALIDATE_TOKEN_ENDPOINT = 'auth/token/validate';
    private FORGOT_PASSWORD_END = 'auth/forgot-password';

    constructor(
        private http: HttpClient,
        private router: Router,
        private cookieService: CookieService
    ) { }

    get auth(): AuthenticatedUser {
        try {
            if (this.cookieService.get(environment.versionControl.env + environment.versionControl.v + 'SessionAuth') &&
                this.cookieService.get(environment.versionControl.env + environment.versionControl.v + 'SessionAuth') !== 'undefined') {
                const cookie = JSON.parse(decodeURIComponent(this.cookieService.get(environment.versionControl.env + environment.versionControl.v + 'SessionAuth')));
                if (cookie.token && cookie.userId) {
                    return {
                        token: cookie.token,
                        userId: cookie.userId,
                        email: cookie.email,
                        name: cookie.name,
                        phoneNumber: cookie.phoneNumber,
                        organisation: cookie.organisation as Organisation,
                        resetPasswordRequired: cookie.resetPasswordRequired,
                        dashboardStats: cookie.dashboardStats as DashboardStats,
                        role: cookie.role as Role
                    }
                }
            }
            this.cookieService.delete(environment.versionControl.env + environment.versionControl.v +
                'SessionAuth', '/', environment.cookieDomain);
            return {} as AuthenticatedUser;
        } catch (error) {
            this.cookieService.delete(environment.versionControl.env + environment.versionControl.v +
                'SessionAuth', '/', environment.cookieDomain);
            return {} as AuthenticatedUser;
        }
    }

    authStatus = new BehaviorSubject(this.auth);

    async login(body: { email: string; password: string }): Promise<ApiResponse<LoginResponse>> {
        const source = await this.http.post(environment.apiBase + this.LOGIN_ENDPOINT, body);
        const response = await lastValueFrom(source) as ApiResponse<LoginResponse>;
        return response;
    }

    public async storeSession(session, isRemember = false): Promise<void> {
        const response = await this.getUserProfile(false, session) as ApiResponse<AuthenticatedUser>;
        if (response.success) {
            const encodeString = JSON.stringify({
                token: session.token.toString(),
                userId: response.data.userId,
                email: response.data.email,
                name: response.data.name,
                phoneNumber: response.data.phoneNumber,
                organisation: response.data.organisation,
                resetPasswordRequired: response.data.resetPasswordRequired,
                // dashboardStats: response.data.dashboardStats,
                role: response.data.role,
            } as AuthenticatedUser);
            const d = new Date();
            const days = isRemember ? 7 : 1;
            d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
            // Remove the cookieDomain for debugging
            await this.cookieService.set(environment.versionControl.env + environment.versionControl.v + 'SessionAuth', encodeString.toString(), {
                expires: d,
                path: '/',
                // domain: environment.cookieDomain,
                secure: true
            });
        } else {
            this.logout();
        }
    }

    public async recreateSession(token: string, user_id: string): Promise<AuthenticatedUser> {
        this.cookieService.delete(environment.versionControl.env + environment.versionControl.v + 'SessionAuth', '/', environment.cookieDomain, true);
        // tslint:disable-next-line: no-unused-expression
        await this.storeSession({ token, user_id }) as unknown;
        const auth = this.auth;
        this.authStatus.next(auth);
        return auth;
    }

    public getUserProfile(resolver = false, session?: { token: string }): Observable<unknown> | Promise<unknown> | unknown {
        // set the headers for the request
        let headers = {};
        if (session && session.token) {
            headers = {
                Authorization: `Bearer ${session.token}`
            };
        }
        const httpCall = this.http.get(environment.apiBase + this.GET_USER_PROFILE_ENDPOINT, {
            headers: headers
        })
        return resolver ? httpCall : lastValueFrom(httpCall);
    }

    public async checkAuthentication(): Promise<any> {
        return lastValueFrom(this.http.get(environment.apiBase + this.VALIDATE_TOKEN_ENDPOINT));
    }

    public logout(): void {
        this.cookieService.delete(environment.versionControl.env + environment.versionControl.v + 'SessionAuth', '/');
        this.authStatus.next({} as AuthenticatedUser);
        this.router.navigate(['/login']);
    }

    public async requestPasswordReset(email: string): Promise<ApiResponse<null>> {
        const params = new HttpParams().set('email', email);
        const source = await this.http.post(environment.apiBase + this.FORGOT_PASSWORD_END, null, { params });
        const response = await lastValueFrom(source) as ApiResponse<null>;
        return response;
    }

    get isStakeholderAdmin(): boolean {
        return this.auth && this.auth.role && this.auth.role.slug === 'stakeholder_admin';
    }

    get isStakeholderUser(): boolean {
        return this.auth && this.auth.role && this.auth.role.slug === 'stakeholder_user';
    }

    get isSuperAdmin(): boolean {
        return this.auth && this.auth.role && this.auth.role.slug === 'advisor_admin';
    }

    get isAdvisorAdmin(): boolean {
        return this.auth && this.auth.role && this.auth.role.slug === 'advisor_user';
    }
}

export interface LoginResponse {
    token: string;
    userId: number;
    email: string;
    name: string;
    organisation: Organisation;
    resetPasswordRequired: boolean;
    role: Role;
    dashboardStats: DashboardStats;
}